import useTicket from '@/hooks/sales/useTicketAttaches';
import { selectCurrency } from '@/redux/config/configSlice';
import { selectActiveTicket, updateTicket } from '@/redux/data/dataSlice';
import { TicketItemType } from '@/redux/data/dataTypes';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { FileTextOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Input, Modal, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;
const { TextArea } = Input;

const CartItem = ({ item }: { item: TicketItemType }) => {
  const dispatch = useAppDispatch();
  const currency = useAppSelector(selectCurrency);
  const activeTicket = useAppSelector(selectActiveTicket);
  const { t } = useTranslation();
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteValue, setNoteValue] = useState(item.notes || '');

  const { updateItem } = useTicket();

  useEffect(() => {
    if (!isNoteModalOpen) {
      setNoteValue(item.notes || '');
    }
  }, [item.notes, isNoteModalOpen]);

  const handleDecrement = useCallback(() => {
    if (!activeTicket) return;

    const newQuantity = Math.max(0, (item.quantity || 0) - 1);
    if (newQuantity === 0) {
      // Remove item if quantity becomes 0
      const newTicket = structuredClone(activeTicket);
      newTicket.items = newTicket.items?.filter((i) => i.ID !== item.ID) || [];
      dispatch(updateTicket(newTicket));
    } else {
      updateItem(item.ID, { quantity: newQuantity });
    }
  }, [activeTicket, dispatch, item.ID, item.quantity, updateItem]);

  const handleIncrement = useCallback(() => {
    const newQuantity = (item.quantity || 0) + 1;
    updateItem(item.ID, { quantity: newQuantity });
  }, [item.ID, item.quantity, updateItem]);

  const handleCardClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('button') || target.closest('.ant-btn')) {
        return;
      }
      setNoteValue(item.notes || '');
      setIsNoteModalOpen(true);
    },
    [item.notes]
  );

  const handleNoteSave = useCallback(() => {
    updateItem(item.ID, { notes: noteValue.trim() || undefined });
    setIsNoteModalOpen(false);
  }, [item.ID, noteValue, updateItem]);

  const handleNoteCancel = useCallback(() => {
    setNoteValue(item.notes || '');
    setIsNoteModalOpen(false);
  }, [item.notes]);

  return (
    <>
      <Card
        className="shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
        styles={{
          body: {
            padding: '8px 10px',
          },
        }}
        onClick={handleCardClick}
      >
        <div className="flex items-start lg:items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <Text strong className="block text-gray-800 text-[11px] lg:text-xs truncate">
                {item.name}
              </Text>
              {item.notes && (
                <FileTextOutlined className="text-[#17527B] text-[10px] lg:text-xs flex-shrink-0" />
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Text type="secondary" className="text-[9px] lg:text-[10px]">
                {item.quantity} × {item.price} {currency}
              </Text>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row items-end lg:items-center gap-1.5 lg:gap-2 flex-shrink-0">
            <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5">
              <Button
                type="text"
                size="small"
                icon={<MinusOutlined className="text-xs" />}
                className="w-5 h-5 lg:w-6 lg:h-6 flex items-center justify-center hover:bg-gray-200 min-w-[20px]"
                onClick={handleDecrement}
              />
              <span className="px-1 text-[9px] lg:text-[10px] font-medium min-w-[16px] lg:min-w-[18px] text-center">
                {item.quantity}
              </span>
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined className="text-xs" />}
                className="w-5 h-5 lg:w-6 lg:h-6 flex items-center justify-center hover:bg-gray-200 min-w-[20px]"
                onClick={handleIncrement}
              />
            </div>

            <div className="text-right">
              <Text strong className="text-xs lg:text-sm text-[#17527B]">
                {((item.quantity || 0) * (item.price || 0)).toFixed(2)}
              </Text>
              <Text type="secondary" className="block text-[9px]">
                {currency}
              </Text>
            </div>
          </div>
        </div>
      </Card>

      <Modal
        title={t('common:itemNotes')}
        open={isNoteModalOpen}
        onOk={handleNoteSave}
        onCancel={handleNoteCancel}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
        centered
        destroyOnClose
      >
        <TextArea
          className="mb-4"
          placeholder={t('common:itemNotesPlaceholder')}
          value={noteValue}
          onChange={(e) => setNoteValue(e.target.value)}
          rows={4}
          autoFocus
          maxLength={500}
          showCount
        />
      </Modal>
    </>
  );
};

export default CartItem;
